import { useCallback } from 'react';
import {
	isDRBehavior,
	isScrollToUDSBehavior,
	routeForBehavior
} from '../constants/drRoutes';
import fireTrackableEvent from '../actions/app';
import useEventBus from './useEventBus';
import { scrollToElement } from '../utils/scrollToElement';

/**
 * Hook to handle clicks on DR CTA buttons on VDP.
 *
 * - vehicle-dr-dashboard-button: scrolls to urlAnchor if available
 * - vehicle-dr-finance-button: emits uds:openSdp:request { route: 'credit' }
 * - vehicle-dr-trade-button: emits uds:openSdp:request { route: 'tradeInSearch' }
 *
 * @param {string} pageAlias - Page identifier to distinguish SRP vs VDP tracking
 * @returns {Function} handleClick - Click handler for DR items
 */
export const useDRClickHandler = (pageAlias = '') => {
	const [eventBus, isReady] = useEventBus();

	const handleClick = useCallback(
		(event, item) => {
			if (!isDRBehavior(item.behavior)) {
				return;
			}
			const route = routeForBehavior(item.behavior);

			try {
				// Update URL to reflect clicked button's route (only if route param exists)
				// Keeps URL in sync with user action for bookmarks & history
				const url = new URL(window.location);
				const hasExistingRoute = url.searchParams.has('route');

				// Only set route if it was already in URL (replace stale value)
				if (hasExistingRoute) {
					url.searchParams.set('route', route);
					window.history.replaceState({}, '', url);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('[ws-vehicle-ctas] Failed to update URL:', err);
			}

			// Dashboard: scroll to anchor if available (e.g., 'mmd-widget')
			if (isScrollToUDSBehavior(item.behavior)) {
				try {
					fireTrackableEvent({
						event: 'Navigate to CTA destination',
						route,
						source: 'cta-editor',
						behavior: item.behavior,
						pageAlias
					});
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error('[ws-vehicle-ctas] tracking failed', err);
				}

				try {
					if (item.urlAnchor && scrollToElement(item.urlAnchor)) {
						return;
					}
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error(
						'[ws-vehicle-ctas] scrollToElement failed',
						err
					);
				}
				return;
			}

			// Fire tracking before preventDefault/emit — must not block primary action if it fails
			try {
				fireTrackableEvent({
					event: 'Navigate to CTA destination',
					route,
					source: 'cta-editor',
					behavior: item.behavior,
					pageAlias
				});
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('[ws-vehicle-ctas] tracking failed', err);
			}

			// If eventBus is missing, let the browser navigate to the synthesized href (fallback)
			if (!isReady) {
				// eslint-disable-next-line no-console
				console.warn('[ws-vehicle-ctas] caiEventBus not ready');
				return;
			}

			event?.preventDefault?.();

			try {
				eventBus.emit('uds:openSdp:request', { route });
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('[ws-vehicle-ctas] eventBus.emit failed', err);
			}
		},
		[pageAlias, eventBus, isReady]
	);

	return handleClick;
};
